import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import ProductMetricCard from './ProductMetricCard';

// Memoized products overview component
const ProductsOverview = React.memo(({
  stats,
  onClearAllFilters,
  onSetFilter,
  onSetSearchMode,
  onSetAdvancedFiltersOpen,
  onFilterSubmit,
  onExportProducts
}) => {
  // Memoized click handlers to prevent recreating on every render
  const handleViewAll = React.useCallback(() => {
    onClearAllFilters();
    onSetSearchMode('basic');
  }, [onClearAllFilters, onSetSearchMode]);

  const handleViewLowStock = React.useCallback(() => {
    onClearAllFilters();
    onSetFilter(prev => ({ ...prev, stock_status: '库存不足' }));
    onSetAdvancedFiltersOpen(true);
    onFilterSubmit();
  }, [onClearAllFilters, onSetFilter, onSetAdvancedFiltersOpen, onFilterSubmit]);

  const handleLowStockButton = React.useCallback(() => {
    onSetFilter(prev => ({ ...prev, stock_status: '库存不足' }));
    onSetAdvancedFiltersOpen(true);
    onFilterSubmit();
  }, [onSetFilter, onSetAdvancedFiltersOpen, onFilterSubmit]);

  // Early return if no stats to prevent unnecessary renders
  if (!stats) return null;

  return (
    <Card className="border mb-6">
      <CardContent className="py-5">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h6 className="text-lg font-semibold">
              📊 产品概览
            </h6>
            <Badge variant="outline" className="text-success border-green-600">
              实时
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-warning border-yellow-600 hover:bg-warning/10"
              onClick={handleLowStockButton}
              aria-label="View low stock products"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              库存不足 (23)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onExportProducts}
              aria-label="Export products data"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              导出
            </Button>
          </div>
        </div>

        {/* Compact Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" role="region" aria-label="Products metrics">
          <ProductMetricCard
            title="产品总数"
            value={stats.total_products}
            color="text-primary"
            bgColor="bg-muted"
            borderColor="border-gray-200"
            tooltipTitle="Click to view all products"
            onClick={handleViewAll}
          />

          <ProductMetricCard
            title="库存不足"
            value={23}
            color="text-warning"
            bgColor="bg-warning/10"
            borderColor="border-warning/20"
            tooltipTitle="Click to filter low stock products"
            onClick={handleViewLowStock}
          />
        </div>
      </CardContent>
    </Card>
  );
});

ProductsOverview.displayName = 'ProductsOverview';

export default ProductsOverview;
